/**
 * App root component - Main application shell
 */

import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { View } from '../models/types.js';
import { databaseService } from '../services/database.js';
import { timerService } from '../services/timer.js';
import { notificationService } from '../services/notifications.js';
import { audioService } from '../services/audio.js';
import { leaderElection } from '../services/leader-election.js';
import SyncService from '../services/sync.js';
import './timer-display.js';
import './check-in-form.js';
import './log-view.js';
import './settings-panel.js';
import './theme-toggle.js';

@customElement('app-root')
export class AppRoot extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100dvh;
      background-color: var(--bg-primary);
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.25rem 0.5rem;
      background-color: var(--bg-secondary);
      border-bottom: 1px solid var(--color-border);
    }

    .title {
      font-size: 0.9rem;
      font-weight: 400;
      color: var(--text-primary);
      margin-right: 1rem;
    }

    .nav {
      display: flex;
      gap: 0.25rem;
      flex: 1;
    }

    .nav-button {
      padding: 0.25rem 0.5rem;
      background: transparent;
      color: var(--text-secondary);
      border: none;
      font-weight: 400;
      transition: none;
    }

    .nav-button:hover {
      background-color: var(--bg-tertiary);
      color: var(--text-primary);
    }

    .nav-button.active {
      background-color: transparent;
      color: var(--text-primary);
      text-decoration: underline;
    }

    .header-actions {
      display: flex;
      align-items: center;
    }

    .main {
      flex: 1;
      overflow-y: auto;
      padding: 0.5rem;
      display: flex;
      flex-direction: column;
    }

    .view {
      max-width: 100%;
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
  `;

  @state() private currentView: View = 'timer';
  @state() private showCheckIn: boolean = false;
  @state() private darkMode: boolean = false;

  private syncService: SyncService | null = null;

  async connectedCallback() {
    super.connectedCallback();

    // Initialize app
    await this.initializeApp();

    // Listen for timer boundary events
    timerService.subscribe((event) => {
      if (event.type === 'boundary') {
        this.handleBoundary();
      }
    });

    // Listen for manual check-in requests
    this.addEventListener('request-check-in', () => {
      this.showCheckIn = true;
    });
  }

  private async initializeApp() {
    try {
      // Load app state
      const appState = await databaseService.getAppState();

      // Apply dark mode
      this.darkMode = appState.settings.darkMode;
      this.applyDarkMode(this.darkMode);

      // Request notification permission if enabled
      if (appState.settings.notificationsEnabled) {
        await notificationService.requestPermission();
      }

      // Configure sync if URL is set
      if (appState.settings.syncUrl) {
        this.syncService = new SyncService(databaseService.getDB());
        await this.syncService.configureSyncUrl(appState.settings.syncUrl);
      }

      // Check if we need to show check-in
      await this.checkIfCheckInNeeded();
    } catch (error) {
      console.error('Failed to initialize app:', error);
    }
  }

  private async checkIfCheckInNeeded() {
    const lastCheckIn = await databaseService.getLastCheckIn();

    if (!lastCheckIn) {
      // First time user - show check-in
      this.showCheckIn = true;
      return;
    }

    // Check if 25+ minutes have passed
    const now = Date.now();
    const elapsed = now - lastCheckIn.checkInTime;
    const minutes = elapsed / (1000 * 60);

    if (minutes >= 25) {
      this.showCheckIn = true;
    }
  }

  private handleBoundary() {
    if (leaderElection.isLeader()) {
      // Show notification (leader only)
      if (notificationService.getPermission() === 'granted') {
        notificationService.showBoundaryNotification();
      }

      // Play sound (leader only)
      databaseService.getAppState().then(state => {
        if (state.settings.soundEnabled) {
          audioService.playAlert();
        }
      });
    }

    // Show check-in form (all tabs)
    this.showCheckIn = true;
  }

  private handleCheckInComplete() {
    this.showCheckIn = false;
  }

  private switchView(view: View) {
    this.currentView = view;
    this.showCheckIn = false;
  }

  private async toggleDarkMode() {
    this.darkMode = !this.darkMode;
    this.applyDarkMode(this.darkMode);

    // Persist to database
    await databaseService.updateAppState({
      settings: {
        ...(await databaseService.getAppState()).settings,
        darkMode: this.darkMode
      }
    });
  }

  private applyDarkMode(enabled: boolean) {
    if (enabled) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  render() {
    return html`
      <div class="header">
        <span class="title">full-life</span>
        <nav class="nav">
          <button
            class="nav-button ${this.currentView === 'timer' ? 'active' : ''}"
            @click=${() => this.switchView('timer')}
          >
            timer
          </button>
          <button
            class="nav-button ${this.currentView === 'log' ? 'active' : ''}"
            @click=${() => this.switchView('log')}
          >
            log
          </button>
          <button
            class="nav-button ${this.currentView === 'settings' ? 'active' : ''}"
            @click=${() => this.switchView('settings')}
          >
            settings
          </button>
        </nav>
        <div class="header-actions">
          <theme-toggle
            .darkMode=${this.darkMode}
            @toggle=${this.toggleDarkMode}
          ></theme-toggle>
        </div>
      </div>

      <main class="main">
        <div class="view">
          ${this.renderView()}
        </div>
      </main>
    `;
  }

  private renderView() {
    if (this.showCheckIn && this.currentView === 'timer') {
      return html`
        <check-in-form
          @check-in-complete=${this.handleCheckInComplete}
        ></check-in-form>
      `;
    }

    switch (this.currentView) {
      case 'timer':
        return html`<timer-display></timer-display>`;
      case 'log':
        return html`<log-view></log-view>`;
      case 'settings':
        return html`<settings-panel></settings-panel>`;
      default:
        return html`<timer-display></timer-display>`;
    }
  }
}
